import { useCallback, useMemo, useState } from "react";
import { emptyProgramGameForm } from "./constants";
import { validateProgramGameForm } from "./formUtils";
import { useProgramGameClubLoaders } from "./hooks/useProgramGameClubLoaders";
import { useProgramGameCompetitionEditionOptions } from "./hooks/useProgramGameCompetitionEditionOptions";
import { useProgramGameFormEffects } from "./hooks/useProgramGameFormEffects";
import { useProgramGameFormLinkHandlers } from "./hooks/useProgramGameFormLinkHandlers";
import { useProgramGameFormSubmit } from "./hooks/useProgramGameFormSubmit";
import { useProgramGameNationalLeagueOptions } from "./hooks/useProgramGameNationalLeagueOptions";
import { useProgramGameTeamOptions } from "./hooks/useProgramGameTeamOptions";
import type {
	OwnershipOption,
	ProgramGameCompetitionEditionOption,
	ProgramGameFormState,
	ProgramGameSelectOption,
} from "./types";

interface UseProgramGameFormOptions {
	isOpen: boolean;
	isAdminUser: boolean;
	ownershipItems: OwnershipOption[];
	typeOptions: ProgramGameSelectOption[];
	onClose: () => void;
}

export function useProgramGameForm({
	isOpen,
	isAdminUser,
	ownershipItems,
	typeOptions,
	onClose,
}: UseProgramGameFormOptions) {
	const [form, setForm] = useState(emptyProgramGameForm);

	const homeClubId = form.homeClub ? Number(form.homeClub.value) : null;
	const awayClubId = form.awayClub ? Number(form.awayClub.value) : null;

	useProgramGameFormEffects({
		isOpen,
		typeOptions,
		isAdminUser,
		ownershipItems,
		setForm,
	});

	const { loadHomeOptions, loadAwayOptions } = useProgramGameClubLoaders();
	const { teamOptions: homeTeamOptions, teamsLoading: homeTeamsLoading } = useProgramGameTeamOptions(
		isOpen,
		homeClubId,
	);
	const { teamOptions: awayTeamOptions, teamsLoading: awayTeamsLoading } = useProgramGameTeamOptions(
		isOpen,
		awayClubId,
	);

	// A selected club with no teams (e.g. a non-partner club) → the team field is optional/disabled.
	const homeClubHasNoTeams = !!form.homeClub && !homeTeamsLoading && homeTeamOptions.length === 0;
	const awayClubHasNoTeams = !!form.awayClub && !awayTeamsLoading && awayTeamOptions.length === 0;

	// National league dropdown appears when "Is National = Yes", scoped to the home club country.
	const isNationalSelected = form.isNational?.value === "true";
	const { nationalLeagueOptions, nationalLeaguesLoading } = useProgramGameNationalLeagueOptions(
		isOpen,
		isNationalSelected,
		homeClubId,
		awayClubId,
	);

	const formErrors = useMemo(
		() => validateProgramGameForm(form, isAdminUser, { homeClubHasNoTeams, awayClubHasNoTeams }),
		[form, isAdminUser, homeClubHasNoTeams, awayClubHasNoTeams],
	);
	const hasErrors = Object.keys(formErrors).length > 0;

	const { isSubmitted, isSubmitting, handleSubmit } = useProgramGameFormSubmit({
		isOpen,
		isAdminUser,
		ownershipItems,
		form,
		hasErrors,
		onClose,
	});

	const { handleOwnershipChange, setTeamsheetLinkAt, setVideoLinkAt } =
		useProgramGameFormLinkHandlers(setForm);

	const updateForm = useCallback(
		(patch: Partial<ProgramGameFormState> | ((prev: ProgramGameFormState) => ProgramGameFormState)) => {
			setForm((prev) => (typeof patch === "function" ? patch(prev) : { ...prev, ...patch }));
		},
		[],
	);

	const setCompetitionEdition = useCallback(
		(edition: ProgramGameCompetitionEditionOption | null) => {
			setForm((prev) => {
				if (prev.competitionEdition?.value === edition?.value) {
					return prev;
				}
				return { ...prev, competitionEdition: edition };
			});
		},
		[],
	);

	const { competitionEditionOptions, competitionEditionsLoading } =
		useProgramGameCompetitionEditionOptions(
			isOpen,
			form.homeTeam,
			form.awayTeam,
			form.competitionEdition,
			setCompetitionEdition,
		);

	return {
		form,
		updateForm,
		isSubmitted,
		isSubmitting,
		formErrors,
		homeTeamOptions,
		awayTeamOptions,
		homeTeamsLoading,
		awayTeamsLoading,
		homeClubHasNoTeams,
		awayClubHasNoTeams,
		nationalLeagueOptions,
		nationalLeaguesLoading,
		isNationalSelected,
		competitionEditionOptions,
		competitionEditionsLoading,
		loadHomeOptions,
		loadAwayOptions,
		handleOwnershipChange,
		setTeamsheetLinkAt,
		setVideoLinkAt,
		handleSubmit,
	};
}
